import { Box, Button } from '@factorialco/deprecated-design-system'
import { TeamDetailFragment, UpdateTeamMutationDocument } from 'generated/resources/graphql'

import Text from 'modules/core/components/Inputs/Text'
import TextArea from 'modules/core/components/Inputs/TextArea'
import Modal from 'modules/core/components/Modal'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import AvatarForm from 'modules/teams/components/AvatarForm'
import TeamForm from 'modules/teams/lib/forms/Team'

interface Props {
  team: TeamDetailFragment
  onClose: () => void
}

const EditModal = ({ team, onClose }: Props) => {
  const form = useUnsafeMemo(() => new TeamForm(team), [team])

  useEffect(() => {
    track(trackingEvents.teams.renameTeamStart, {
      team_id: team.id,
    })
  }, [team.id])

  const [mutate] = useMutation(UpdateTeamMutationDocument, {
    extractorFn: (data) => data.teamsMutations.updateTeamsTeam,
  })

  const onSubmit = async () => {
    const nameChanged = form.get('name').value !== team.name

    mutate({
      variables: {
        id: team.id,
        name: form.get('name').value,
        description: form.get('description').value,
      },
      optimisticResponse: {
        teamsMutations: {
          updateTeamsTeam: {
            errors: [],
            team: {
              ...team,
              name: form.get('name').value,
              description: form.get('description').value,
            },
          },
        },
      },
      onCompleted: () => {
        track(trackingEvents.teams.saveEditTeamInformationSubmit, {
          team_id: team.id,
        })
        if (nameChanged) {
          track(trackingEvents.teams.renameTeamSubmit, {
            team_id: team.id,
          })
        }
        onClose()
      },
    })
  }

  return (
    <Modal
      illustration="input-field"
      title={i18n.t('teams.edit')}
      description={i18n.t('teams.define_team')}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={!form.get('name')} submit>
            {i18n.t('common.save')}
          </Button>
        </>
      }
    >
      <Box gap="s24">
        <AvatarForm team={team} />
        <Text label={i18n.t('teams.name')} field={form.get('name')} />
        <TextArea label={i18n.t('teams.description_label')} field={form.get('description')} />
      </Box>
    </Modal>
  )
}

export default EditModal
